/**
 * Decides what the client should do when MoveMatchingDoneEvent fires.
 *
 * Lifted out of the results store so it can be unit-tested without mocking
 * Pinia / vue-router / Pusher. Keeps the store a thin wiring layer.
 *
 * The "stash" case is what fixes CU-86d2jf6c5: matching can finish on the
 * backend while the customer is still entering an OTP. We don't navigate away
 * in that case — the results store parks the intent and
 * session.verifyPhoneCode drains it after phone_verified flips.
 */

export interface LeadDistributionShape {
    already_distributed?: boolean;
    delivered_company_ids?: number[];
    can_opt_in?: boolean;
    can_opt_in_pre_contact?: boolean;
}

export type MatchingDoneDecision =
    | { kind: 'ignore' }
    | { kind: 'navigate'; recommended: boolean }
    | { kind: 'stash'; recommended: boolean };

export interface MatchingDoneInput {
    routePath: string;
    hasRecommendedMatches: boolean;
    isLocal: boolean;
    isInterstate: boolean;
    leadDistribution: LeadDistributionShape | null | undefined;
    requiresPhoneVerification: boolean;
    phoneVerified: boolean;
}

export function decideMatchingDoneNavigation(input: MatchingDoneInput): MatchingDoneDecision {
    if (input.routePath === '/results/review') {
        return { kind: 'ignore' };
    }

    const ld = input.leadDistribution;
    const delivered = ld?.delivered_company_ids ?? [];
    const hasLeadDistributionDeliveries =
        Boolean(ld?.already_distributed) || (Array.isArray(delivered) && delivered.length > 0);
    const canShowLeadDistributionTile = input.isLocal && ld?.can_opt_in === true && !hasLeadDistributionDeliveries;

    // Distributed local customers belong on /results/recommended (post-
    // distribution success surface) regardless of MUVEXPRESS coverage. Without
    // this clause the Echo MoveMatchingDoneEvent listener routes them to
    // /results/organic and they bounce back via the organic route guard —
    // wasteful and prone to race conditions if session.retrieve() returns a
    // stale lead_distribution payload. See CU-86d2uzj6n / W78YN audit. Same
    // shape as the matching `form_step` decision (`Move::triggerMatching`)
    // and the three router guards (`router/index.js`, `resultRoutes.js` x2).
    const isDistributedLocal = input.isLocal && hasLeadDistributionDeliveries;

    // Pre-contact-eligible local customer: they're in the lead-selling pipeline
    // (region live, credits available, all system gates passed) but `can_opt_in`
    // is still false because PHONE_NOT_VERIFIED is in the quality reasons. They
    // will flip to `can_opt_in = true` once phone verification completes — at
    // which point Recommended.vue's tile renders. Route them to
    // /results/recommended now so the tile shows IMMEDIATELY when the stashed
    // nav drains post-phone-verify (instead of routing to /results/organic
    // which stays even after the gate flips). MoveMatchingDoneEvent is the
    // ONLY guard that fires pre-phone-verification (via Echo as soon as
    // matching completes), so it's the only one that needs this pre-contact
    // bridge — the four router/Loading.vue guards all run post-verify.
    // See CU-86d2uzj6n / N98KW audit.
    const isPreContactEligibleLocal =
        input.isLocal && ld?.can_opt_in_pre_contact === true && !hasLeadDistributionDeliveries;

    // Interstate orders are NOT special-cased: with recommended matches they
    // go to /results/recommended, otherwise /results/organic — which shows the
    // organic match list, or the custom-quote CTA when there are no matches at
    // all. Routing interstate straight to recommended hid real organic matches
    // (PR #378 regression CU-86d2gg2y6).
    const recommended = Boolean(
        input.hasRecommendedMatches || canShowLeadDistributionTile || isDistributedLocal || isPreContactEligibleLocal,
    );

    if (input.requiresPhoneVerification && !input.phoneVerified) {
        return { kind: 'stash', recommended };
    }

    return { kind: 'navigate', recommended };
}
